import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import userService from '../../../services/userService';
import '../../../pages/Pages.css';
import './AllUsers.css';

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    email: '',
    mobile_number: '',
    address: '',
    role_type: '',
    permissions: []
  });
  const [addFormData, setAddFormData] = useState({
    full_name: '',
    email: '',
    mobile_number: '',
    address: '',
    role_type: '',
    password: '',
    confirm_password: '',
    status: 'active',
    permissions: []
  });

  useEffect(() => {
    loadUsers();
    loadPermissions();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await userService.getAllPermissions();
      setPermissions(response.data);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load permissions', 'error');
    }
  };

  const loadRoles = async () => {
    try {
      const response = await userService.getAllRoles();
      setRoles(response.data);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load roles', 'error');
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await userService.getUserById(userId);
      setSelectedUser(response.data);
      setShowViewModal(true);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load user details', 'error');
    }
  };

  const handleEditUser = async (user) => {
    setSelectedUser(user);
    const userPermissions = Array.isArray(user.permissions) 
      ? user.permissions 
      : JSON.parse(user.permissions || '[]');
    
    setEditFormData({
      full_name: user.full_name,
      email: user.email,
      mobile_number: user.mobile_number,
      address: user.address,
      role_type: user.role_type,
      permissions: userPermissions
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await userService.updateUser(selectedUser.id, editFormData);
      Swal.fire('Success!', 'User updated successfully', 'success');
      setShowEditModal(false);
      loadUsers();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to update user', 'error');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (addFormData.password !== addFormData.confirm_password) {
      Swal.fire('Error!', 'Passwords do not match', 'error');
      return;
    }

    if (addFormData.password.length < 6) {
      Swal.fire('Error!', 'Password must be at least 6 characters long', 'error');
      return;
    }

    try {
      const userData = {
        full_name: addFormData.full_name,
        email: addFormData.email,
        mobile_number: addFormData.mobile_number,
        address: addFormData.address,
        role_type: addFormData.role_type,
        password: addFormData.password,
        status: addFormData.status,
        permissions: addFormData.permissions
      };

      await userService.createUser(userData);
      Swal.fire('Success!', 'User created successfully', 'success');
      setShowAddModal(false);
      resetAddForm();
      loadUsers();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to create user', 'error');
    }
  };

  const resetAddForm = () => {
    setAddFormData({
      full_name: '',
      email: '',
      mobile_number: '',
      address: '',
      role_type: '',
      password: '',
      confirm_password: '',
      status: 'active',
      permissions: []
    });
  };

  const handleDeleteUser = (user) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${user.full_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await userService.deleteUser(user.id);
          Swal.fire('Deleted!', 'User has been deleted.', 'success');
          loadUsers();
        } catch (error) {
          Swal.fire('Error!', error.message || 'Failed to delete user', 'error');
        }
      }
    });
  };

  const handleStatusUpdate = (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activate' : 'deactivate';
    
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to ${action} ${user.full_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus === 'active' ? '#3085d6' : '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Yes, ${action} it!`
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await userService.updateUserStatus(user.id, newStatus);
          Swal.fire('Updated!', `User has been ${action}d.`, 'success');
          loadUsers();
        } catch (error) {
          Swal.fire('Error!', error.message || `Failed to ${action} user`, 'error');
        }
      }
    });
  };

  const handlePermissionChange = (permissionId, isChecked, formType) => {
    const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
    const formData = formType === 'add' ? addFormData : editFormData;

    if (isChecked) {
      setFormData({
        ...formData,
        permissions: [...formData.permissions, permissionId]
      });
    } else {
      setFormData({
        ...formData,
        permissions: formData.permissions.filter(id => id !== permissionId)
      });
    }
  };

  const handleSelectAllPermissions = (formType) => {
    const setFormData = formType === 'add' ? setAddFormData : setEditFormData;
    const formData = formType === 'add' ? addFormData : editFormData;
    const allPermissionIds = permissions.map(p => p.id);

    if (formData.permissions.length === permissions.length) {
      // If all are selected, deselect all
      setFormData({
        ...formData,
        permissions: []
      });
    } else {
      // Select all
      setFormData({
        ...formData,
        permissions: allPermissionIds
      });
    }
  };

  const formatPermissions = (userPermissions) => {
    if (Array.isArray(userPermissions)) {
      return permissions
        .filter(p => userPermissions.includes(p.id))
        .map(p => p.name)
        .join(', ');
    }
    
    try {
      const parsed = JSON.parse(userPermissions || '[]');
      return permissions
        .filter(p => parsed.includes(p.id))
        .map(p => p.name)
        .join(', ');
    } catch {
      return userPermissions || 'No permissions';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>All Users</h1>
          <p>Manage system users</p>
        </div>
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1>All Users</h1>
            <p>Manage system users ({users.length} users found)</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={loadUsers}>
              🔄 Refresh
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => setShowAddModal(true)}
            >
              ➕ Add New User
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Employee ID</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.full_name}</td>
                  <td>{user.email}</td>
                  <td>{user.emp_id}</td>
                  <td>
                    <span className={`role-badge role-${user.role_type.toLowerCase().replace(' ', '-')}`}>
                      {user.role_type}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-${user.status}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-view"
                        onClick={() => handleViewUser(user.id)}
                        title="View User"
                      >
                        👁️
                      </button>
                      <button 
                        className="btn btn-edit"
                        onClick={() => handleEditUser(user)}
                        title="Edit User"
                      >
                        ✏️
                      </button>
                      <button 
                        className={`btn btn-status ${user.status === 'active' ? 'btn-inactive' : 'btn-active'}`}
                        onClick={() => handleStatusUpdate(user)}
                        title={user.status === 'active' ? 'Deactivate User' : 'Activate User'}
                      >
                        {user.status === 'active' ? '⏸️' : '▶️'}
                      </button>
                      <button 
                        className="btn btn-delete"
                        onClick={() => handleDeleteUser(user)}
                        title="Delete User"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {users.length === 0 && (
            <div className="no-data">
              No users found
            </div>
          )}
        </div>
      </div>

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>User Details</h3>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-details">
                <div className="detail-row">
                  <label>Full Name:</label>
                  <span>{selectedUser.full_name}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{selectedUser.email}</span>
                </div>
                <div className="detail-row">
                  <label>Employee ID:</label>
                  <span>{selectedUser.emp_id}</span>
                </div>
                <div className="detail-row">
                  <label>Mobile Number:</label>
                  <span>{selectedUser.mobile_number}</span>
                </div>
                <div className="detail-row">
                  <label>Address:</label>
                  <span>{selectedUser.address}</span>
                </div>
                <div className="detail-row">
                  <label>Role Type:</label>
                  <span>{selectedUser.role_type}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge status-${selectedUser.status}`}>
                    {selectedUser.status}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Permissions:</label>
                  <span>{formatPermissions(selectedUser.permissions)}</span>
                </div>
                <div className="detail-row">
                  <label>Created At:</label>
                  <span>{formatDate(selectedUser.created_at)}</span>
                </div>
                <div className="detail-row">
                  <label>Updated At:</label>
                  <span>{formatDate(selectedUser.updated_at)}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>Edit User - {selectedUser?.full_name}</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={editFormData.full_name}
                      onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Mobile Number *</label>
                    <input
                      type="tel"
                      value={editFormData.mobile_number}
                      onChange={(e) => setEditFormData({...editFormData, mobile_number: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Role Type *</label>
                    <select
                      value={editFormData.role_type}
                      onChange={(e) => setEditFormData({...editFormData, role_type: e.target.value})}
                      required
                    >
                      <option value="">Select Role</option>
                      {roles.map(role => (
                        <option key={role.id} value={role.name}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Address</label>
                    <textarea
                      value={editFormData.address}
                      onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                      rows="3"
                    />
                  </div>
                  <div className="form-group full-width">
                    <div className="permissions-header">
                      <label>Permissions</label>
                      <button 
                        type="button" 
                        className="btn btn-link"
                        onClick={() => handleSelectAllPermissions('edit')}
                      >
                        {editFormData.permissions.length === permissions.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="permissions-grid">
                      {permissions.map(permission => (
                        <label key={permission.id} className="permission-checkbox">
                          <input
                            type="checkbox"
                            checked={editFormData.permissions.includes(permission.id)}
                            onChange={(e) => handlePermissionChange(permission.id, e.target.checked, 'edit')}
                          />
                          <span>{permission.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>Add New User</h3>
              <button className="modal-close" onClick={() => {setShowAddModal(false); resetAddForm();}}>×</button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={addFormData.full_name}
                      onChange={(e) => setAddFormData({...addFormData, full_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={addFormData.email}
                      onChange={(e) => setAddFormData({...addFormData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Mobile Number *</label>
                    <input
                      type="tel"
                      value={addFormData.mobile_number}
                      onChange={(e) => setAddFormData({...addFormData, mobile_number: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Role Type *</label>
                    <select
                      value={addFormData.role_type}
                      onChange={(e) => setAddFormData({...addFormData, role_type: e.target.value})}
                      required
                    >
                      <option value="">Select Role</option>
                      {roles.map(role => (
                        <option key={role.id} value={role.name}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status *</label>
                    <select
                      value={addFormData.status}
                      onChange={(e) => setAddFormData({...addFormData, status: e.target.value})}
                      required
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      value={addFormData.password}
                      onChange={(e) => setAddFormData({...addFormData, password: e.target.value})}
                      required
                      minLength="6"
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm Password *</label>
                    <input
                      type="password"
                      value={addFormData.confirm_password}
                      onChange={(e) => setAddFormData({...addFormData, confirm_password: e.target.value})}
                      required
                      minLength="6"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Address</label>
                    <textarea
                      value={addFormData.address}
                      onChange={(e) => setAddFormData({...addFormData, address: e.target.value})}
                      rows="3"
                    />
                  </div>
                  <div className="form-group full-width">
                    <div className="permissions-header">
                      <label>Permissions</label>
                      <button 
                        type="button" 
                        className="btn btn-link"
                        onClick={() => handleSelectAllPermissions('add')}
                      >
                        {addFormData.permissions.length === permissions.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="permissions-grid">
                      {permissions.map(permission => (
                        <label key={permission.id} className="permission-checkbox">
                          <input
                            type="checkbox"
                            checked={addFormData.permissions.includes(permission.id)}
                            onChange={(e) => handlePermissionChange(permission.id, e.target.checked, 'add')}
                          />
                          <span>{permission.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => {setShowAddModal(false); resetAddForm();}}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllUsers;